/**
 * Collections + daily habit state, per profile: stickers stuck in the album, creature cards already
 * opened, the frame chosen for each drawing on the gallery wall and the weekly rhythm goal.
 *
 * The data lives in synced progress (`progress.collect`, core collect/state.ts) inside the main app
 * store (`sketchling-state-v1`), so it follows the profile across devices. The old local-only store
 * (`sketchling-collect-v1`) is folded in once on load (state/store.ts `migrateLegacyCollect`).
 *
 * This module keeps the previous hook API (`useCollectStore(selector)`, `useCollectProfile()`,
 * `EMPTY_COLLECT`) so the screens did not have to change.
 *
 * RULE (as in state/store.ts): selectors return stored references or stable constants; the plain view
 * is derived in useMemo.
 */
import { useMemo } from 'react';
import { collectionFrames, collectionGoal, DEFAULT_WEEKLY_GOAL, openCards, placeStickers, setCollectionFrame, setCollectionGoal, type CollectionState, type FrameId } from '@sketchling/core';
import { EMPTY_COLLECTION, useApp, useCollection } from '../../state/store';

export interface CollectProfileState {
  /** Album sticker ids already peeled and stuck in. */
  placed: string[];
  /** Creature cards (drawing ids) opened at least once (clears the "New!" badge). */
  opened: string[];
  /** Gallery wall frame per journal item. */
  frames: Record<string, FrameId>;
  /** Weekly rhythm goal (drawings per week). */
  goal: number;
}

export const EMPTY_COLLECT: CollectProfileState = { placed: [], opened: [], frames: {}, goal: DEFAULT_WEEKLY_GOAL };

export interface CollectActions {
  place(profileId: string, ids: string[]): void;
  open(profileId: string, drawingIds: string[]): void;
  setFrame(profileId: string, journalId: string, frame: FrameId): void;
  setGoal(profileId: string, goal: number): void;
}

const update = (pid: string, fn: (c: CollectionState) => CollectionState) => useApp.getState().updateCollect(pid, fn);

const ACTIONS: CollectActions = {
  place: (pid, ids) => update(pid, (c) => placeStickers(c, ids)),
  open: (pid, ids) => update(pid, (c) => openCards(c, ids)),
  setFrame: (pid, jid, frame) => update(pid, (c) => setCollectionFrame(c, jid, frame, new Date().toISOString())),
  setGoal: (pid, goal) => update(pid, (c) => setCollectionGoal(c, goal, new Date().toISOString())),
};

/** Collect actions (stable functions): `useCollectStore((s) => s.place)`. */
export function useCollectStore<T>(selector: (s: CollectActions) => T): T {
  return selector(ACTIONS);
}
useCollectStore.getState = (): CollectActions => ACTIONS;

/** Plain view of a synced collection (frames → frame ids, goal → number). */
export function collectView(c: CollectionState): CollectProfileState {
  if (c === EMPTY_COLLECTION) return EMPTY_COLLECT;
  return { placed: c.placed, opened: c.opened, frames: collectionFrames(c) as Record<string, FrameId>, goal: collectionGoal(c) };
}

/** Collect state of the active profile (stable EMPTY_COLLECT when none; memoised per stored object). */
export function useCollectProfile(): CollectProfileState {
  const c = useCollection();
  return useMemo(() => collectView(c), [c]);
}
